import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "@phosphor-icons/react";
import { BUSINESS } from "@/data/site";

// Standard Privacy Policy for a home service dispatch app.
// Written for Google Play Store compliance. Update the "Last updated" date
// whenever material changes are made.
const LAST_UPDATED = "July 10, 2026";
const CONTACT_EMAIL = "david.josephindustries@gmail.com";
const BUSINESS_ADDRESS = "Southern Louisiana, USA";

export default function PrivacyPage() {
  return (
    <div data-testid="privacy-page">
      <section className="ji-container pt-16 md:pt-24 pb-8">
        <Link to="/" data-testid="privacy-back-home" className="text-xs uppercase tracking-[0.22em] text-[var(--ji-muted)] hover:text-[var(--ji-primary)] inline-flex items-center gap-1 mb-6">
          <ArrowLeft size={12} weight="bold" /> Back to home
        </Link>
        <div className="ji-overline mb-5 inline-flex items-center gap-2">
          <ShieldCheck size={12} weight="fill" /> Legal
        </div>
        <h1 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }} className="text-5xl md:text-6xl font-bold tracking-tight">
          Privacy Policy
        </h1>
        <p className="mt-5 text-sm text-[var(--ji-muted)]">Last updated: {LAST_UPDATED}</p>
      </section>

      <section className="ji-container pb-24 max-w-3xl">
        <article className="prose prose-neutral prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-3 prose-p:text-[var(--ji-text)] prose-p:leading-relaxed text-[var(--ji-text)] space-y-6">
          <p className="text-base leading-relaxed">
            {BUSINESS.name} ("we," "us," or "our") operates the josephindustries.site website and any related
            mobile applications (the "Service"). This Privacy Policy explains what information we collect from
            you when you request a home service — appliance repair, installation, courier, cleanup, junk removal,
            moving, assembly, or heavy lifting — how we use it, and the choices you have.
          </p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>1. Information We Collect</h2>
          <p><strong>Contact information</strong> you voluntarily provide when you request a quote, book a
          diagnostic, submit a review, apply for a job, or purchase a part through our shop:</p>
          <ul className="list-disc pl-6 space-y-1 text-sm leading-relaxed">
            <li>Full name</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Physical service address (street, city, state, ZIP code)</li>
            <li>Appliance make/model or job description you send us</li>
            <li>Any photos or attachments you choose to send</li>
          </ul>

          <p><strong>Location data</strong> is collected in two ways:</p>
          <ul className="list-disc pl-6 space-y-1 text-sm leading-relaxed">
            <li>You enter your service address or ZIP code on our website so we can dispatch the correct crew and check for local parts availability near you.</li>
            <li>If you use a future mobile app version, we may request access to your device's precise GPS location <em>only when you tap "Request a technician"</em> — this helps us route the closest available crew to you. You may deny or revoke this permission at any time in your device settings; the Service still works without it.</li>
          </ul>

          <p><strong>Payment information</strong> — when you pay a deposit, diagnostic fee, or invoice, payment
          card details are entered directly on PayPal's secure checkout page. We <strong>never see or store</strong> your
          full card number. We only receive a payment confirmation ID, the amount, and the last 4 digits.</p>

          <p><strong>Automatic technical data</strong> — like most websites, our servers automatically log
          your IP address, browser type, pages visited, and timestamps. This is used to detect abuse
          (rate-limiting), diagnose bugs, and improve the site.</p>

          <p><strong>Local storage on your device</strong> — we use browser localStorage to remember non-sensitive
          preferences like your recently-viewed products and whether you've dismissed the "Refer &amp; Earn"
          promotional chip. This data never leaves your device.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>2. How We Use Your Information</h2>
          <ul className="list-disc pl-6 space-y-1 text-sm leading-relaxed">
            <li>To dispatch a technician to your service address and complete the requested job</li>
            <li>To contact you by phone, text, or email about your quote, appointment, or invoice</li>
            <li>To source appliance parts and check local supplier stock near your ZIP code</li>
            <li>To send you a receipt, invoice, or job-completion follow-up</li>
            <li>To send you a referral credit notification when a friend uses your code</li>
            <li>To respond to reviews you post and improve service quality</li>
            <li>To comply with applicable law (tax reporting, subpoenas, safety)</li>
          </ul>
          <p>We do <strong>not</strong> sell, rent, or trade your personal information to third parties for marketing.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>3. Who We Share Data With</h2>
          <p>We share the minimum information necessary with the following categories of service providers:</p>
          <ul className="list-disc pl-6 space-y-1 text-sm leading-relaxed">
            <li><strong>PayPal</strong> — to process payments (name, email, amount)</li>
            <li><strong>Resend</strong> — to deliver transactional emails (name, email, message content)</li>
            <li><strong>MongoDB / hosting infrastructure</strong> — for secure storage of your service request</li>
            <li><strong>Amazon</strong> — when you click an affiliate product link, Amazon sees only that click and their own cookies (governed by Amazon's privacy policy)</li>
            <li><strong>Our subcontracted crew members</strong> — the name, phone, address, and job description of the specific customer they are dispatched to</li>
          </ul>
          <p>We may also disclose information if compelled by law, court order, or to protect our rights, property, or safety.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>4. How Long We Keep It</h2>
          <p>Service request records, invoices, and reviews are retained for up to 7 years to comply with
          U.S. tax and record-keeping requirements. Job applications are retained for up to 2 years.
          Localstorage data on your device stays until you clear your browser data.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>5. Security</h2>
          <p>Data is transmitted over HTTPS (TLS 1.2+). Passwords for our admin accounts are hashed with
          bcrypt. Payment card data never touches our servers. No system is perfectly secure — you use the
          Service at your own risk, and we recommend using a strong, unique password if you create an account.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>6. Your Rights</h2>
          <p>You may at any time email us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--ji-primary)] underline">{CONTACT_EMAIL}</a> to:</p>
          <ul className="list-disc pl-6 space-y-1 text-sm leading-relaxed">
            <li>Request a copy of the personal information we hold about you</li>
            <li>Correct or update your information</li>
            <li>Delete your information (subject to legal record-keeping obligations)</li>
            <li>Withdraw consent for marketing communications</li>
            <li>File a complaint about how we've handled your data</li>
          </ul>
          <p>If you are a California resident, you have additional rights under the CCPA/CPRA including the right
          to opt out of "sharing" — as noted above, we do not sell or share personal information for
          cross-context behavioral advertising.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>7. Children</h2>
          <p>The Service is not directed at children under 13. We do not knowingly collect personal information
          from children under 13. If you believe we have, please contact us and we will delete it promptly.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>8. Changes to This Policy</h2>
          <p>We may update this policy from time to time. Material changes will be posted here with a new
          "Last updated" date. Continued use of the Service after a change constitutes acceptance.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>9. Contact</h2>
          <p>Questions or requests? Contact us:</p>
          <address className="not-italic text-sm leading-relaxed">
            <strong>{BUSINESS.name}</strong><br />
            {BUSINESS_ADDRESS}<br />
            Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--ji-primary)] underline">{CONTACT_EMAIL}</a><br />
            Phone: <a href={`tel:${BUSINESS.phones[0].tel}`} className="text-[var(--ji-primary)] underline">{BUSINESS.phones[0].display}</a>
          </address>
        </article>
      </section>
    </div>
  );
}
